import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    InputSignal,
    OnDestroy,
    OnInit,
    output,
    OutputEmitterRef,
    Signal,
    signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {onEach} from '../../../utils/observable-utils';
import {ResponsiveService} from "../../../responsive/responsive.service";
import {OrderModel} from '../../../order/order.model';
import {ContentMessageComponent} from "../../../notification/message/content-message.component";
import {MessageLevel} from "../../../notification/message/message";
import {PaymentModel} from "../../payment.model";
import {CardPaymentSubmissionResult, PaymentAuthorisationType, PaymentCard} from '../../payment';
import {SpinnerComponent} from "../../../ui-elements/spinner/spinner.component";
import {SelectorControlComponent} from '../../../inputs/selector-control/selector-control.component';
import {ConfigurationModel} from '../../../configuration/configuration.model';

@Component({
    selector: "woa-adyen-manual-payment-card-panel",
    templateUrl: "./adyen-manual-payment-card-panel.component.html",
    styleUrl: "./adyen-manual-payment-card-panel.component.scss",
    host: {
        "[class.disabled]": "disabled()"
    },
    providers: [{provide: PaymentCard, useExisting: AdyenManualPaymentCardPanelComponent}],
    // styleUrl was moved here
    imports: [
        ContentMessageComponent,
        SelectorControlComponent,
        SpinnerComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdyenManualPaymentCardPanelComponent extends PaymentCard implements OnInit, AfterViewInit, OnDestroy {
    private destroyRef: DestroyRef = inject(DestroyRef);
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private responsiveService: ResponsiveService = inject(ResponsiveService);
    private orderModel: OrderModel = inject(OrderModel);
    private paymentModel: PaymentModel = inject(PaymentModel);

    public readonly disabled: InputSignal<boolean> = input<boolean>(false);
    public readonly hidden: InputSignal<boolean> = input<boolean>(false);

    public onPayFormReady: OutputEmitterRef<boolean> = output<boolean>();

    cardHolder: Signal<ElementRef> = viewChild.required("cardHolder");

    MessageLevel = MessageLevel;

    public readonly payFormError: WritableSignal<string> = signal<string>(undefined);
    private readonly submitted: WritableSignal<boolean> = signal<boolean>(false);
    private readonly payFormReady: WritableSignal<boolean> = signal<boolean>(false);

    public readonly showSpinner: Signal<boolean> = computed<boolean>(() =>
        !this.hidden() && (!this.paymentModel.paymentPlatformPublicKey() || (!this.payFormReady() && !this.payFormError())));

    public readonly showSavePaymentDetailsOption: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.isConsumerActor());

    public savePaymentDetails: boolean = false;
    private hasBeenUnhidden: boolean = false;
    private setupStarted: boolean = false;
    private adyenCheckout: any;
    private adyenCard: any;
    private submissionResult: CardPaymentSubmissionResult = new CardPaymentSubmissionResult(PaymentAuthorisationType.ADYEN_PAYMENT_METHOD);
    private destroyed: boolean = false;

    constructor() {
        super();
        this.savePaymentDetails = this.responsiveService.isMobile() || this.configurationModel.devOrDemoMode();

        effect(() => {
            if (!this.hidden()) {
                if (!this.hasBeenUnhidden) {
                    setTimeout(() => this.setup());
                }
                this.hasBeenUnhidden = true;
            }
        });
    }

    ngOnInit(): void {
        onEach(this.paymentModel.paymentDetailsApplied$, () => this.setup(), this.destroyRef);
    }

    async ngAfterViewInit(): Promise<void> {
        return this.setup();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        if (this.adyenCard) {
            this.adyenCard.unmount();
        }
    }

    public canSavePaymentDetails(): boolean {
        return this.showSavePaymentDetailsOption() && this.savePaymentDetails;
    }

    public isValid(): boolean {
        this.submitted.set(true);
        if (!this.adyenCard) {
            return false;
        }
        if (!this.adyenCard.isValid) {
            this.adyenCard.showValidation();
            return false;
        }
        return true;
    }

    public markAsUnsubmitted(): void {
        this.submitted.set(false);
    }

    public async submit(): Promise<CardPaymentSubmissionResult> {
        this.submissionResult.token = btoa(JSON.stringify(this.adyenCard.data));
        return this.submissionResult;
    }

    private async setup(): Promise<void> {
        if (this.destroyed || !this.hasBeenUnhidden || this.setupStarted || !this.paymentModel.paymentPlatformPublicKey() || !this.cardHolder()) {
            return;
        }

        this.setupStarted = true;
        this.adyenCheckout = await (<any>window).AdyenWeb.AdyenCheckout({
            clientKey: this.paymentModel.paymentPlatformPublicKey(),
            environment: this.paymentModel.paymentPlatformLiveMode() ? "live-au" : "test",
            locale: "en_US",
            countryCode: "AU",
            paymentMethodsResponse: JSON.parse(this.paymentModel.paymentMethods()),
            amount: {
                value: Math.floor(this.orderModel.initiatedOrder().orderTotal * 100),
                currency: "AUD"
            },
            showPayButton: false
        });
        // noinspection JSUnusedGlobalSymbols
        this.adyenCard = new (<any>window).AdyenWeb.Card(this.adyenCheckout, {
            onConfigSuccess: () => {
                this.payFormReady.set(true);
                setTimeout(() => this.onPayFormReady.emit(true), 250);
            },
            onBrand: (result: {
                brand: string
            }) => this.submissionResult.brand = result.brand,
            onFieldValid: (result: {
                fieldType: string,
                endDigits: string
            }) => {
                if (result.fieldType === "encryptedCardNumber") {
                    this.submissionResult.last4 = result.endDigits;
                }
            },
            onBinLookup: (result: {
                issuingCountryCode: string
            }) => this.submissionResult.country = result.issuingCountryCode,
            onError: (error: string) => {
                console.error("Adyen card error", error);
                this.payFormError.set(error);
                setTimeout(() => this.onPayFormReady.emit(false), 300);
            }
        }).mount(this.cardHolder().nativeElement);
    }
}
